import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseIcon } from '../../../assets/icons/BlockActionIcons';
import { SimpleDialog } from '../../../components/UI/SimpleDialog/index';
import { DRAFT } from '../../../constants/draft.constants';
import { UPDATE_DRAFT_CATEGORIES_MUTATION } from '../../../graphql/resolvers/draftMutations/updateDraftCategories.gql';
import { extractedCategories } from '../../../helpers/extractedCategories';
import type {
  AutoCompleteCategories,
  ChipData,
  DraftInterfaceState,
} from '../../../interfaces/post.interface';
import { draftSlice } from '../../../store/draft.store';
import './draft-categories.scss';

const DraftCategories = ({ draftId }: { draftId: string }) => {
  const dispatch = useDispatch();
  const { categories, refreshDraft } = useSelector(
    (state: DraftInterfaceState) => state.draftStore,
  );
  const { SAVE_DRAFT_CATEGORIES, REFRESH_DRAFT_STORE_ACTION } =
    draftSlice.actions;
  const { REMOVE_CATEGORY, CANCEL, REMOVE } = DRAFT.REMOVE_CATEGORY_DIALOG;

  const [savedCategories, setSavedCategories] = React.useState<ChipData[]>([]);
  const [pendingDelete, setPendingDelete] = React.useState<ChipData | null>(
    null,
  );

  React.useEffect(() => {
    if (categories) {
      const categoriesArray = categories.map((value, index) => ({
        key: index,
        label: value,
      }));
      setSavedCategories(categoriesArray);
    }
  }, [categories]);

  const [updateDraftCategories, { loading }] = useMutation(
    UPDATE_DRAFT_CATEGORIES_MUTATION,
    {
      variables: { input: undefined },
      onError: (ex) => {
        console.error('useMutation error =>', ex);
      },
    },
  );

  const handleDeleteIntent = (chip: ChipData) => () => {
    setPendingDelete(chip);
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;

    const updated = savedCategories.filter(
      (chip) => chip.key !== pendingDelete.key,
    );
    setSavedCategories(updated);
    setPendingDelete(null);

    const payload = extractedCategories(updated as AutoCompleteCategories[]);
    dispatch(SAVE_DRAFT_CATEGORIES(payload));

    updateDraftCategories({
      variables: { input: { categories: payload, id: draftId } },
    })
      .then(() => dispatch(REFRESH_DRAFT_STORE_ACTION(true)))
      .catch((e) => console.error(e))
      .finally(() => dispatch(REFRESH_DRAFT_STORE_ACTION(false)));
  };

  const handleCancelDelete = () => {
    setPendingDelete(null);
  };

  const renderChips = savedCategories.map((data) => {
    try {
      return (
        <span key={data.key} className="chip">
          {data.label}
          <button
            type="button"
            onClick={handleDeleteIntent(data)}
            aria-label={`Remove ${data.label}`}
          >
            <CloseIcon size={12} />
          </button>
        </span>
      );
    } catch (ex) {
      console.error('Error loading categories, ex: ', ex);
    }
  });

  return (
    <>
      <div
        className="DRAFT__CATEGORIES__CONTAINER"
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '0.375rem',
          alignItems: 'center',
          opacity: loading ? 0.5 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        {renderChips}
      </div>

      <SimpleDialog
        className={'REMOVE__CATEGORIES__MODAL'}
        open={!!pendingDelete}
        onClose={handleCancelDelete}
        onSubmit={handleConfirmDelete}
        dialogTitle={REMOVE_CATEGORY}
        dialogBody={
          pendingDelete
            ? `Remove "${pendingDelete.label}" from this draft? This will be saved immediately.`
            : ''
        }
        closeModalLabel={CANCEL}
        actionLabel={REMOVE}
        maxWidth="xs"
        fullWidth
      />
    </>
  );
};

export default DraftCategories;
